import React, { useEffect, useState, useRef } from 'react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import dayjs from 'dayjs';
// import { Bar, BarChart, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ZAxis } from 'recharts';
import { Bar, BarChart, XAxis, YAxis, Tooltip, ResponsiveContainer, Customized, Cell } from 'recharts';
import api from '../utils/api';
import { CircularProgress, Box, Typography } from '@mui/material';
import InfoButtonWithTooltip from './Tooltips';
// Convert timestamp string to number (milliseconds)
const toMs = (time) => dayjs(time).valueOf();

const SERIES = [
  { name: 'household_served', color: '#2563eb', label: 'Households' },
  { name: 'camel_served',     color: '#16a34a', label: 'Camels' },
  { name: 'goat_served',      color: '#f59e0b', label: 'Goats' },
  { name: 'cattle_served',    color: '#ef4444', label: 'Cattle' },
  { name: 'donkey_served',    color: '#8b5cf6', label: 'Donkeys' },
  { name: 'tankers_served',   color: '#0ea5e9', label: 'Tankers' },
];

const ChartWithSlider = ({ minTime, maxTime, setMinTime, setMaxTime, range, setRange, monthyear, setMonthYear, selectedRegions, selectedDistricts }) => {
    // Sort the data by timestamp
    const [reports, setReports] = useState([]);
    const [sortedData, setSortedData] = useState([]);
    const [tempRange, setTempRange] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMonths, setSelectedMonths] = useState(() => new Set()); // indices
    const [selectedYears, setSelectedYears]   = useState(() => new Set()); // strings like "2024"
    const lastClickRef = useRef(null);
    
    const hasSelection = selectedMonths.size > 0 || selectedYears.size > 0;

    const isActiveIdx = (idx) => {
    const y = monthData[idx]?.year;
        return selectedMonths.has(idx) || (y && selectedYears.has(y));
    };

    const toggleMonth = (idx, evt) => {
        const additive = evt?.metaKey || evt?.ctrlKey;
        const rangeSel = evt?.shiftKey && lastClickRef.current != null;

        setSelectedMonths(prev => {
            const next = new Set(prev);

            if (rangeSel) {
                const a = lastClickRef.current;
                const [lo, hi] = a < idx ? [a, idx] : [idx, a];
                for (let i = lo; i <= hi; i++) next.add(i);
            } else if (additive) {
                next.has(idx) ? next.delete(idx) : next.add(idx);
            } else {
                // single select (replace)
                next.clear();
                next.add(idx);
            }

            return next;
        });

        lastClickRef.current = idx;
    };

    const toggleYear = (year, evt) => {
        const additive = evt?.metaKey || evt?.ctrlKey;

        setSelectedYears(prev => {
            if (additive) {
                const next = new Set(prev);
                next.has(year) ? next.delete(year) : next.add(year);
                return next;
            } else {
                // single year replace
                return prev.size === 1 && prev.has(year) ? new Set() : new Set([year]);
            }
        });
    };

  useEffect(() => {
    const controller = new AbortController();
    api.get('yield-water/', {
                params: {
                    region:JSON.stringify(selectedRegions),
                    district:JSON.stringify(selectedDistricts)
                },
                signal: controller.signal
        }).then((res) => {
            const data = JSON.parse(res.data).map((item, index) => ({
            id: index + 1, // DataGrid requires an `id` field
            visits: item['yield'],
            ...item,
        }));
        // console.log(data);
        setReports(data);
      })
      .catch((err) => {
        console.error('Failed to fetch reports:', err);
      })
      .finally(() => setLoading(false));
       return () => {
        controller.abort();
       };
  }, [selectedRegions, selectedDistricts]);

  useEffect(() => {
    if (reports.length > 0) {
      const sorted = [...reports].sort((a, b) => toMs(a.today) - toMs(b.today));
      setSortedData(sorted);
      setMinTime(toMs(sorted[0].today));
      setMaxTime(toMs(sorted[sorted.length - 1].today));
      setTempRange([toMs(sorted[0].today), toMs(sorted[sorted.length - 1].today)])
      setRange([toMs(sorted[0].today), toMs(sorted[sorted.length - 1].today)]);
    }
  }, [reports]);

  const filteredData = sortedData.filter((d) => {
    const time = toMs(d.today);
    return time >= range[0] && time <= range[1];
  });

  const { monthData, years, yearSpans } = React.useMemo(() => {
    const map = new Map();           // 'YYYY-MM' -> row
    const indicesByYear = new Map(); // year -> [row indices]
    const yearSet = new Set();

    for (const d of filteredData) {
        // console.log(d);
        const dt = dayjs(d.today);
        const ym = dt.format('YYYY-MM');
        const year = dt.format('YYYY');
        const monthNum = dt.format('MMM');          // 1..12
        const monthLabel = dt.format('MMM YYYY');

        yearSet.add(year);
        // if (!map.has(ym)) map.set(ym, { ym, monthLabel, year, monthNum });
        if (!map.has(ym)) {
            const base = { ym, monthLabel, year, monthNum };
            SERIES.forEach(k => { base[k.name] = 0; });
            map.set(ym, base);
        }
        const row = map.get(ym);
        SERIES.forEach(k => { row[k.name] += Number(d[k.name] || 0); });
        // console.log(d, row);
        row[year] = (row[year] || 0) + (Number(d.visits) || 0);
    }

    const data = Array.from(map.values()).sort((a, b) => a.ym.localeCompare(b.ym));

    data.forEach((row, idx) => {
        const arr = indicesByYear.get(row.year) || [];
        arr.push(idx);
        indicesByYear.set(row.year, arr);
    });

    const spans = [];
    indicesByYear.forEach((idxs, y) => {
        spans.push({ year: y, startIdx: idxs[0], endIdx: idxs[idxs.length - 1] });
    });

    return {
        monthData: data,
        years: Array.from(yearSet).sort(),
        yearSpans: spans,
    };
    }, [filteredData]);

    const YearLabelsLayer = React.useMemo(
        () => (props) => (
            <GroupedYearLabels
            {...props}
            monthData={monthData}
            yearSpans={yearSpans}
            // avoid relying on refs that may be null on first paint
            // compute y inside your GroupedYearLabels using props.offset
            />
        ),
        [monthData, yearSpans]
    );

    useEffect(() => {
        if (!hasSelection){
            setSelectedYears(new Set([dayjs().format('YYYY')]))
        }
    }, [hasSelection]);

    const selectedIdxs = React.useMemo(() => {
        const s = new Set(selectedMonths);
        selectedYears.forEach(y => {
            monthData.forEach((r, i) => { if (r.year === y) s.add(i); });
        });
        return Array.from(s).sort((a,b) => a - b); // [0,1,5,...]
    }, [selectedMonths, selectedYears, monthData]);

    const selectedMonthKeys = React.useMemo(
        () => selectedIdxs.map(i => monthData[i]?.ym),
        [selectedIdxs, monthData]
    );

    const prevKeysRef = useRef('');
    useEffect(() => {
        const nextStr = selectedMonthKeys.join(',');
        // console.log('nextstr', nextStr);
        // console.log('currstr', prevKeysRef.current);
        if (prevKeysRef.current !== nextStr) {
            // console.log('tidak sama');
            prevKeysRef.current = nextStr;
            setMonthYear(selectedMonthKeys);
        }
    }, [selectedMonthKeys, setMonthYear]);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const row = monthData.find(r => r.monthLabel === label);
            return (
                <div style={{
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: 6,
                padding: 10,
                boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
                fontSize: '13px',
                zIndex:1000,
                // position: 'absolute',
                }}>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                    {dayjs(label).format('MMM YYYY')}
                </div>
                {/* {payload.map((entry, index) => (
                    <div key={index} style={{ color: entry.color }}>
                    <strong>{entry.value}</strong> visits
                    </div>
                ))} */}
                {SERIES.map(k => (
                    <div key={k.name} style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                    {/* <span style={{ textTransform:'capitalize' }}>{k.name.replace(/_/g,' ')}</span> */}
                    <span style={{ textTransform:'capitalize', color:k.color }}>{k.label.replace(/_/g,' ')}</span>
                    <b>{row?.[k.name].toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) ?? 0}</b>
                    </div>
                ))}
                </div>
            );
        }
        return null;
    };

    const GroupedYearLabels = ({ xAxisMap, offset, monthData, yearSpans }) => {
        const xAxis = Object.values(xAxisMap)[0];
        const scale = xAxis?.scale;
        const band  = xAxis?.bandSize || 0;
        if (!scale || !offset) return null;

        const xCenter = (i) => (scale(monthData[i].monthLabel) + band/2)+0;
        const y = offset.top + offset.height + 18; // inside plot; avoids clipping on reload

        return (
            <g>
            {yearSpans.map((s) => {
                const mid = Math.floor((s.startIdx + s.endIdx) / 2);
                const active = selectedYears.has(s.year);
                // clickable span area for the whole year group
                const x1 = scale(monthData[s.startIdx].monthLabel);
                const x2 = scale(monthData[s.endIdx].monthLabel) + band;
                return (
                <g key={s.year}>
                    {/* <rect
                    x={x1}
                    y={y - 14}
                    width={x2 - x1}
                    height={20}
                    fill="rgba(0,0,0,0)"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => toggleYear(s.year, e)}
                    /> */}
                    <text
                        x={xCenter(mid)}
                        y={y}
                        dy={12}
                        textAnchor="middle"
                        fontSize={12}
                        fontWeight={active ? 800 : 400}
                        fill={active ? '#111' : '#4B5563'}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => toggleYear(s.year, e)}
                        >
                        {s.year}
                    </text>
                </g>
                );
            })}
            </g>
        );
    };

    const XAxisClickZones = ({ xAxisMap, offset, monthData, onSelect }) => {
        const xAxis = Object.values(xAxisMap)[0];
        const scale = xAxis?.scale;
        const band  = xAxis?.bandSize || 0;
        if (!scale) return null;

        const rectHeight = 22;
        const y = offset.top + offset.height + 4; // below plotting area (under labels)

        return (
            <g>
            {monthData.map((row, idx) => (
                <rect
                key={`xz-${idx}`}
                x={scale(row.monthLabel)}
                y={y}
                width={band}
                height={rectHeight}
                fill="rgba(0,0,0,0)"
                style={{ cursor: 'pointer' }}
                onClick={(e) => toggleMonth(idx, e)}
                />
            ))}
            </g>
        );
    };

  return (
    <div style={{ width: '100%', zIndex:1000, paddingBottom:0 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
          <CircularProgress size={30} />
        </Box>
      ) : (
        <>
  
          <ResponsiveContainer width="100%" height={90}>
            <Box
                sx={{
                    position:'absolute',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 1,
                    mb: 0.5,
                    top:5,
                    p:0,
                    left:0,
                    zIndex:100,
                    width:10,
                    height:10
                }}>
                <InfoButtonWithTooltip title={'Click on year or hold and click on command/control keyboard for select/unselect multiple selections'}>Info</InfoButtonWithTooltip>
            </Box> 
            <BarChart data={monthData}>
              <XAxis
                    dataKey="monthLabel"
                    tickLine={false}
                    axisLine={false}
                    interval={0}
                    height={28}  // a bit more room for the grouped year labels below
                    tick={({ x, y, index }) => {
                        const label = monthData[index]?.monthNum ?? '';
                        const active = isActiveIdx(index);
                        return (
                        <text
                            x={x}
                            y={y + 10}
                            textAnchor="middle"
                            fontSize={active ? 12 : 11}
                            fontWeight={active ? 700 : 400}
                            fill={active ? '#111' : '#666'}
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => toggleMonth(index, e)}
                        >
                            {label}
                        </text>
                        );
                    }}
                />

                <YAxis allowDecimals={false} tickLine={false} width={40} axisLine={false} hide={true}/>
                <Tooltip content={<CustomTooltip />}/>
                
                {SERIES.map((s) => (
                    <Bar
                        key={s.name}
                        dataKey={s.name}
                        name={s.label}
                        isAnimationActive={false}
                        // onClick={(_, idx) => toggleMonth(idx)}
                    >
                        {monthData.map((row, idx) => {
                            const active = isActiveIdx(idx);
                            const base = s.color;
                            const faded = (hex => {
                                let c = hex.replace('#',''); if (c.length===3) c = c.split('').map(ch=>ch+ch).join('');
                                const [r,g,b] = [0,2,4].map(i=>parseInt(c.slice(i,i+2),16));
                                return `rgba(${r},${g},${b},0.5)`;
                            })(base);
                            return (
                                <Cell
                                key={`cell-${s.name}-${idx}`}
                                cursor="pointer"
                                fill={hasSelection ? (active ? base : faded) : base}
                                stroke={active ? '#111' : undefined}
                                strokeWidth={active ? 0.1 : 0}
                                radius={active ? [4,4,0,0] : 0}
                                />
                            );
                        })}
                    </Bar>
                ))}
                <Customized
                    key={`yl-${monthData.length}-${yearSpans.length}`}
                    component={(props) => (
                        <GroupedYearLabels
                        {...props}
                        monthData={monthData}
                        yearSpans={yearSpans}
                        />
                    )}
                />
            </BarChart>
          </ResponsiveContainer>

         
        </>
    )}
    </div>
  );
};

export default ChartWithSlider;